// Stateful core of the workplace messages surface — single source of truth for
// unread counts, threads, and handlers consumed by ActivityBannerPopover.
import { useCallback, useEffect, useState } from "react";
import {
  acknowledgeOpsNotification,
  archiveOpsNotification,
  createOpsThread,
  createTestOpsNotification,
  getOpsSummary,
  listOpsNotificationMembers,
  listOpsMessageThreads,
  listOpsThreadMessages,
  markAllOpsNotificationsRead,
  markOpsNotificationsReadByIds,
  postOpsThreadReply,
} from "@/lib/opsPaperworkClient";
import {
  filterMessageableMembers,
  archiveSkippedReasonsMessage,
  normalizeContactVisibilityLevel,
} from "@/lib/messages/workplaceMessagesPolicy";
import { isWorkplaceReminderNotificationKind } from "@/lib/messages/workplaceReminderModel";
import { GOOGLE_CALENDAR_INBOUND_KIND } from "@/lib/messages/googleCalendarInboundModel";
import { CHAT_ACTIVITY_BRIDGE_KIND } from "@/lib/messages/chatActivityBridgeModel";
import { useMessageAlertDing } from "./useMessageAlertDing";
import type { MemberRow, NotifRow, ParticipantReceipt, ThreadRow, ViewerInfo } from "./workplaceMessagesTypes";

export type UseWorkplaceMessagesStateResult = {
  capable: boolean | null;
  unreadCount: number;
  unackedCount: number;
  threads: ThreadRow[];
  selectedThreadId: string | null;
  messages: NotifRow[];
  participantReceipts: ParticipantReceipt[];
  members: MemberRow[];
  /** Full org roster (includes L1 + self) for channel membership admin. */
  allOrgMembers: MemberRow[];
  viewer: ViewerInfo | null;
  recipientId: string;
  setRecipientId: (v: string) => void;
  newThreadTitle: string;
  setNewThreadTitle: (v: string) => void;
  newThreadText: string;
  setNewThreadText: (v: string) => void;
  newThreadSenderLocked: boolean;
  setNewThreadSenderLocked: (v: boolean) => void;
  newThreadRequiresAck: boolean;
  setNewThreadRequiresAck: (v: boolean) => void;
  newThreadAckDeadline: string;
  setNewThreadAckDeadline: (v: string) => void;
  replyText: string;
  setReplyText: (v: string) => void;
  replyRequiresAck: boolean;
  setReplyRequiresAck: (v: boolean) => void;
  errorText: string;
  loadingList: boolean;
  testBusy: boolean;
  markBusy: boolean;
  replyBusy: boolean;
  archiveBusy: boolean;
  reduceMotion: boolean;
  loadOnOpen: () => void;
  refreshSummary: () => Promise<void>;
  onMarkAll: () => Promise<void>;
  onTest: () => Promise<void>;
  onCreateThread: () => Promise<void>;
  onSendReply: () => Promise<void>;
  onSelectThread: (threadId: string) => void;
  onMarkOne: (id: string) => Promise<void>;
  onAcknowledge: (id: string) => Promise<void>;
  onArchiveOne: (id: string) => Promise<void>;
  onArchiveThread: (threadId: string) => Promise<void>;
};

export function useWorkplaceMessagesState(): UseWorkplaceMessagesStateResult {
  const [capable, setCapable] = useState<boolean | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unackedCount, setUnackedCount] = useState(0);
  const [threads, setThreads] = useState<ThreadRow[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<NotifRow[]>([]);
  const [participantReceipts, setParticipantReceipts] = useState<ParticipantReceipt[]>([]);
  const [replyText, setReplyText] = useState("");
  const [newThreadText, setNewThreadText] = useState("");
  const [newThreadTitle, setNewThreadTitle] = useState("");
  const [recipientId, setRecipientId] = useState("");
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [allOrgMembers, setAllOrgMembers] = useState<MemberRow[]>([]);
  const [viewer, setViewer] = useState<ViewerInfo | null>(null);
  const [newThreadSenderLocked, setNewThreadSenderLocked] = useState(false);
  const [newThreadRequiresAck, setNewThreadRequiresAck] = useState(false);
  const [newThreadAckDeadline, setNewThreadAckDeadline] = useState("");
  const [replyRequiresAck, setReplyRequiresAck] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [markBusy, setMarkBusy] = useState(false);
  const [replyBusy, setReplyBusy] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  // Audible ping on inbox growth — defaults on, user-toggleable from the panel.
  useMessageAlertDing(unreadCount);

  const refreshSummary = useCallback(async () => {
    const r = await getOpsSummary();
    if (r.status === 401 || r.status === 403) { setCapable(false); return; }
    if (r.ok && r.data && typeof r.data === "object" && "unread_notifications" in r.data) {
      setCapable(true);
      const d = r.data as { unread_notifications?: number; unacknowledged_notifications?: number };
      setUnreadCount(typeof d.unread_notifications === "number" ? d.unread_notifications : 0);
      setUnackedCount(typeof d.unacknowledged_notifications === "number" ? d.unacknowledged_notifications : 0);
    } else {
      setCapable(true);
      setUnreadCount(0);
      setUnackedCount(0);
    }
  }, []);

  useEffect(() => {
    void refreshSummary();
    const id = window.setInterval(() => void refreshSummary(), 60_000);
    return () => window.clearInterval(id);
  }, [refreshSummary]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setErrorText("");
    const r = await listOpsMessageThreads();
    setLoadingList(false);
    if (r.ok && r.data && typeof r.data === "object" && "threads" in r.data) {
      const payload = r.data as { threads: ThreadRow[]; unread_count?: number; unacked_count?: number };
      const dmThreads = (payload.threads ?? []).filter((t) => {
        const kind = String(t.latest_message?.kind || "");
        return !isWorkplaceReminderNotificationKind(kind) && kind !== GOOGLE_CALENDAR_INBOUND_KIND && kind !== CHAT_ACTIVITY_BRIDGE_KIND;
      });
      setThreads(dmThreads);
      // Use callback-form setters so this callback's identity never depends
      // on unreadCount / unackedCount. Otherwise loadList's identity flips
      // every time we update those counts, which in turn flips loadOnOpen
      // and any consumer effect keyed on it -- the root of the
      // ActivityBannerPopover "load messages inbox repeatedly" loop.
      setUnreadCount((prev) =>
        typeof payload.unread_count === "number" ? payload.unread_count : prev
      );
      setUnackedCount((prev) =>
        typeof payload.unacked_count === "number" ? payload.unacked_count : prev
      );
    } else {
      setErrorText(r.ok ? "" : r.error || "Unable to load workplace threads.");
      setThreads([]);
      setMessages([]);
      setSelectedThreadId(null);
    }
  }, []);

  const loadMembers = useCallback(async () => {
    const r = await listOpsNotificationMembers();
    if (r.ok && r.data && typeof r.data === "object" && "members" in r.data) {
      const payload = r.data as { members: MemberRow[]; viewer?: ViewerInfo };
      const allRows = (payload.members ?? []).filter((m) => m.user_id);
      const viewerRow = payload.viewer ?? null;
      setViewer(viewerRow);
      setAllOrgMembers(allRows);
      // Gate the recipient dropdown by the viewer's contact level so a sales
      // (L1) user can't pick a manager (L3) — the server enforces the same
      // rule on POST (`canSendThreadMessage`), but pre-filtering avoids
      // confusing "not allowed" errors after typing a message.
      const dmable = viewerRow
        ? filterMessageableMembers(
            allRows,
            viewerRow.user_id,
            normalizeContactVisibilityLevel(viewerRow.trust_level)
          )
        : allRows.filter((m) => m.user_id);
      setMembers(dmable);
      // Callback-form setter so loadMembers stays referentially stable.
      setRecipientId((prev) => {
        if (prev && dmable.some((m) => m.user_id === prev)) return prev;
        return dmable.length > 0 ? dmable[0]!.user_id : "";
      });
    }
  }, []);

  const loadThreadMessages = useCallback(async (threadId: string) => {
    const r = await listOpsThreadMessages(threadId);
    if (r.ok && r.data && typeof r.data === "object" && "messages" in r.data) {
      const payload = r.data as { messages: NotifRow[]; participant_receipts?: ParticipantReceipt[] };
      setMessages(payload.messages ?? []);
      setParticipantReceipts(payload.participant_receipts ?? []);
    } else {
      setMessages([]);
      setParticipantReceipts([]);
    }
  }, []);

  const loadOnOpen = useCallback(() => {
    void loadList();
    void loadMembers();
    void refreshSummary();
  }, [loadList, loadMembers, refreshSummary]);

  const onMarkOne = useCallback(
    async (id: string) => {
      setMarkBusy(true);
      await markOpsNotificationsReadByIds([id]);
      await loadList();
      await refreshSummary();
      setMarkBusy(false);
    },
    [loadList, refreshSummary]
  );

  const onAcknowledge = useCallback(
    async (id: string) => {
      setMarkBusy(true);
      await acknowledgeOpsNotification({ id });
      if (selectedThreadId) await loadThreadMessages(selectedThreadId);
      await loadList();
      await refreshSummary();
      setMarkBusy(false);
    },
    [selectedThreadId, loadList, loadThreadMessages, refreshSummary]
  );

  const onSendReply = useCallback(async () => {
    if (!selectedThreadId || !replyText.trim()) return;
    setReplyBusy(true);
    const res = await postOpsThreadReply({
      thread_id: selectedThreadId,
      body: replyText.trim(),
      requires_ack: replyRequiresAck || undefined,
    });
    if (!res.ok) {
      setErrorText(res.error || "Unable to send reply.");
      setReplyBusy(false);
      return;
    }
    setReplyText("");
    setReplyRequiresAck(false);
    await loadThreadMessages(selectedThreadId);
    await loadList();
    await refreshSummary();
    setReplyBusy(false);
  }, [selectedThreadId, replyText, replyRequiresAck, loadThreadMessages, loadList, refreshSummary]);

  const onCreateThread = useCallback(async () => {
    if (!newThreadText.trim() || !recipientId) return;
    setReplyBusy(true);
    setErrorText("");
    const ackDeadlineIso =
      newThreadRequiresAck && newThreadAckDeadline.trim()
        ? new Date(newThreadAckDeadline).toISOString()
        : null;
    const res = await createOpsThread({
      body: newThreadText.trim(),
      title: newThreadTitle.trim() || "Workplace message",
      recipient_user_ids: [recipientId],
      sender_locked: newThreadSenderLocked || undefined,
      requires_ack: newThreadRequiresAck || undefined,
      ack_deadline_at: ackDeadlineIso,
    });
    if (res.ok && res.data?.thread_id) {
      setSelectedThreadId(res.data.thread_id);
      setNewThreadText("");
      setNewThreadTitle("");
      setNewThreadSenderLocked(false);
      setNewThreadRequiresAck(false);
      setNewThreadAckDeadline("");
      await loadThreadMessages(res.data.thread_id);
    } else if (!res.ok) {
      setErrorText(res.error || "Unable to create thread.");
    }
    await loadList();
    await refreshSummary();
    setReplyBusy(false);
  }, [
    newThreadText,
    newThreadTitle,
    recipientId,
    newThreadSenderLocked,
    newThreadRequiresAck,
    newThreadAckDeadline,
    loadThreadMessages,
    loadList,
    refreshSummary,
  ]);

  const onMarkAll = useCallback(async () => {
    setMarkBusy(true);
    await markAllOpsNotificationsRead();
    await loadList();
    await refreshSummary();
    setMarkBusy(false);
  }, [loadList, refreshSummary]);

  const onTest = useCallback(async () => {
    setTestBusy(true);
    await createTestOpsNotification();
    await loadList();
    await refreshSummary();
    setTestBusy(false);
  }, [loadList, refreshSummary]);

  const onSelectThread = useCallback(
    (threadId: string) => {
      setSelectedThreadId(threadId);
      void loadThreadMessages(threadId);
    },
    [loadThreadMessages]
  );

  const onArchiveOne = useCallback(
    async (id: string) => {
      setArchiveBusy(true);
      setErrorText("");
      const res = await archiveOpsNotification({ id });
      if (!res.ok) {
        const gateMsg = archiveSkippedReasonsMessage(res.skipped);
        setErrorText(gateMsg || res.error || "Unable to archive message.");
      }
      if (selectedThreadId) await loadThreadMessages(selectedThreadId);
      await loadList();
      await refreshSummary();
      setArchiveBusy(false);
    },
    [selectedThreadId, loadThreadMessages, loadList, refreshSummary]
  );

  const onArchiveThread = useCallback(
    async (threadId: string) => {
      setArchiveBusy(true);
      setErrorText("");
      const res = await archiveOpsNotification({ thread_id: threadId });
      if (!res.ok) {
        const gateMsg = archiveSkippedReasonsMessage(res.skipped);
        setErrorText(gateMsg || res.error || "Unable to archive thread.");
      } else if (res.data?.skipped?.length) {
        const blocked = res.data.skipped.length;
        const archived = res.data.archived ?? 0;
        if (archived === 0) {
          const gateMsg = archiveSkippedReasonsMessage(res.data.skipped);
          setErrorText(gateMsg || `Cannot archive thread yet — ${blocked} message(s) gated.`);
        }
      }
      if (selectedThreadId === threadId) setSelectedThreadId(null);
      await loadList();
      await refreshSummary();
      setArchiveBusy(false);
    },
    [selectedThreadId, loadList, refreshSummary]
  );

  return {
    capable,
    unreadCount,
    unackedCount,
    threads,
    selectedThreadId,
    messages,
    participantReceipts,
    members,
    allOrgMembers,
    viewer,
    recipientId,
    setRecipientId,
    newThreadTitle,
    setNewThreadTitle,
    newThreadText,
    setNewThreadText,
    newThreadSenderLocked,
    setNewThreadSenderLocked,
    newThreadRequiresAck,
    setNewThreadRequiresAck,
    newThreadAckDeadline,
    setNewThreadAckDeadline,
    replyText,
    setReplyText,
    replyRequiresAck,
    setReplyRequiresAck,
    errorText,
    loadingList,
    testBusy,
    markBusy,
    replyBusy,
    archiveBusy,
    reduceMotion,
    loadOnOpen,
    refreshSummary,
    onMarkAll,
    onTest,
    onCreateThread,
    onSendReply,
    onSelectThread,
    onMarkOne,
    onAcknowledge,
    onArchiveOne,
    onArchiveThread,
  };
}
